import type { ReactNode } from "react";

/**
 * The "there is nothing here" panel.
 *
 * An empty list has two causes and they need different words. Nothing exists
 * yet is a state to be helped out of -- the answer is a way to create the first
 * one. Nothing matches your filter is a state to be RECOVERED from, and the
 * answer is a way back to the unfiltered list.
 *
 * THIS COMPONENT DOES NOT MAKE THAT DISTINCTION FOR YOU, which an earlier
 * version of this comment implied. It has no `reason` prop and no branch: the
 * caller writes the sentence and decides whether there is a way out to offer.
 * What it supplies is the shape, so the two cases at least look alike and the
 * `action` slot is there to be filled rather than invented per screen.
 *
 * `action` is optional because a few empties are genuinely terminal, but a
 * screen passing nothing is asserting there is nothing useful to offer, which
 * is worth having to decide.
 */
export function EmptyState({
	title,
	hint,
	action,
}: {
	/** What is empty, in the operator's words. Not "No data". */
	title: string;
	/** Why it might be empty, or what to do about it. */
	hint?: string;
	action?: ReactNode;
}) {
	return (
		<div className="grid justify-items-center gap-2 rounded-2xl border border-dashed bg-slate-50 px-4 py-12 text-center">
			<p className="text-sm font-medium text-slate-700">{title}</p>
			{hint ? <p className="text-sm text-muted-foreground">{hint}</p> : null}
			{action ? <div className="pt-1">{action}</div> : null}
		</div>
	);
}
